import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ChipWithCount from "shared-ui/src/components/ChipWithCount";
import { useEffect } from "react";
import { AssetLifecycleManagementDrawerOrdersItemDropdown } from "./AssetLifecycleManagementDrawerOrdersItemDropdown";
import { useGetAssetLifecycleManagementDrawerOrdersData } from "./hooks/useGetAssetLifecycleManagementDrawerOrdersData";
import {
  GetLifecycleManagementDrawerOrdersSearchRequest,
  LifecycleManagementAllEvents,
} from "@lib/ShiOneClient";
import { formatCurrency } from "@utils/formatters/formatCurrency";

const AssetLifecycleManagementDrawerOrders = ({
  selectedLifecycleEventData,
  onSaveHandlerChange,
}: {
  selectedLifecycleEventData: LifecycleManagementAllEvents | undefined;
  onSaveHandlerChange?: (handler: (() => void) | undefined) => void;
}) => {
  useEffect(() => {
    onSaveHandlerChange?.(undefined);
  }, [onSaveHandlerChange]);

  const { data, isLoading, isError, refetch } =
    useGetAssetLifecycleManagementDrawerOrdersData({
      uniqueIdentifiers: selectedLifecycleEventData?.allUniqueIdentifiers || [],
    } as unknown as GetLifecycleManagementDrawerOrdersSearchRequest);

  const shouldHideOrderDetailsByDefault =
    data?.orders && data?.orders?.length > 1;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress color="inherit" size={50} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          textAlign: "center",
          mt: 4,
          px: 2,
        }}
      >
        <Typography variant="body2">
          There has been an error loading the order details right now. Please
          try again later.
        </Typography>
        <Button onClick={() => refetch()} size="small" variant="outlined">
          Retry
        </Button>
      </Box>
    );
  }

  if (data?.totalOrders === 0 || (data?.orders?.length ?? 0) === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          textAlign: "center",
          mt: 4,
          px: 2,
        }}
      >
        <Typography variant="body2">
          No orders are associated with this event.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ display: "flex", gap: 1, p: 2 }}>
        <ChipWithCount
          singularLabel="Order"
          pluralLabel="Orders"
          count={data?.totalOrders ?? "N/A"}
        />
        <ChipWithCount
          singularLabel="Covered Asset"
          pluralLabel="Covered Assets"
          count={data?.totalCoveredItems ?? "N/A"}
        />
        <ChipWithCount
          singularLabel="Previous Purchase Cost"
          pluralLabel="Previous Purchase Cost"
          count={formatCurrency(data?.totalPreviousPurchaseCost || 0)}
        />
      </Box>
      <Box sx={{ borderTop: "1px solid #D0D0D0" }}>
        {data?.orders?.map((order, index) => (
          <AssetLifecycleManagementDrawerOrdersItemDropdown
            key={order?.order ?? order?.poNumber ?? index}
            data={order}
            shouldHideOrderDetailsByDefault={shouldHideOrderDetailsByDefault}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AssetLifecycleManagementDrawerOrders;
